//================================================================
//  Component: SubmitFinalApprovalPane
//================================================================

//  Purpose: This side pane allows a user to submit a request for Final Approval

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the sidePane}
//    - task = { useState, stores the currently selected request}
//    - finalApprovers = { useState, contains all the final approvers}

//  Example:
//  
//    <SubmitFinalApprovalPane
//      paneOpen={openPane}
//      setPaneOpen={setOpenPane}
//      task={task}
//    ></SubmitFinalApprovalPane>

//================================================================

// Libraries
import React, { useContext , useState} from 'react';
import { useForm } from 'react-hook-form';

// Contexts
import { GetUser } from '../../../../../../Library/GlobalContexts';

// Functions
import WriteDocument from '../../../../../../Library/WriteDocument';
import CreateRequest from '../../../../../../Library/CreateRequest';

// Components

// Images
import Loading from '../../../../../../Components/Images/Image_Loading_Ripple.svg'


export default function SubmitFinalApprovalPane({
    paneOpen,
    setPaneOpen,
    task,
    finalApprovers
}) {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, watch, handleSubmit, reset, formState: { errors } } = useForm();

    // --------------------------------------------------
    //  useState
    // --------------------------------------------------
        
    // Used to set pane status > 'onload', 'pending', 'error'
    const [paneStatus, setPaneStatus] = useState('onload');

    // --------------------------------------------------
    //  Functions
    // --------------------------------------------------

    // Submit Handler
    const onSubmit = (data) => {

        setPaneStatus('pending');

        CreateRequest(4)
        .then(async (requestIds) => {

            const docPromises = [];

            // Finance Approval Task
            const financeApprovalTask = {
                'requestid': requestIds[0],
                'parentrequestid': task?.parentrequestid,
                'tasktype': 'Finance Approval',
                'taskname': task?.requesttype,
                'taskdescription': `Finance Approval for ${task?.applicationname === '' || task?.applicationname === undefined ? task?.initiativename : task?.applicationname}`,
                'approverrole': '',
                'applicationname': task?.applicationname,
                'initiativename': task?.initiativename,
                'stage': '',
                'status': 'Initialised',
                'assignmentgroup': 'Demand Management',
                'requesttype': task?.requesttype,
                'activityfeedvisible': true,
                'approvalhubvisible': true,
                'region': '',
                'finalapprovalcomment': data?.comment,
                'assignedto': {
                    'email': task?.financemanager?.email,
                    'givenname': task?.financemanager?.givenname,
                    'surname': task?.financemanager?.surname
                },
                'assigneddate': new Date(),
                'createddate': new Date(),
                'createdby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'requestowner': task?.createdby,
                'activityfeed': [
                    {
                        'activitydate': new Date(),
                        'actionedby': {
                            'email': task?.financemanager?.email,
                            'givenname': task?.financemanager?.givenname,
                            'surname': task?.financemanager?.surname
                        },
                        'action': 'assigned',
                        'comments': ''
                    }
                ]
            }

            docPromises.push(
                WriteDocument('requests', financeApprovalTask?.requestid, financeApprovalTask, true)
            );

            // Final Approval Task
            const finalApprovalTask = {
                'requestid': requestIds[1],
                'parentrequestid': task?.parentrequestid,
                'tasktype': 'Final Approval',
                'taskname': task?.requesttype,
                'taskdescription': `Final Approval for ${task?.applicationname === '' || task?.applicationname === undefined ? task?.initiativename : task?.applicationname}`,
                'approverrole': task?.businessunit,
                'applicationname': task?.applicationname,
                'initiativename': task?.initiativename,
                'stage': '',
                'status': 'Initialised',
                'assignmentgroup': 'Demand Management',
                'requesttype': task?.requesttype,
                'activityfeedvisible': true,
                'approvalhubvisible': true,
                'region': '',
                'finalapprovalcomment': data?.comment,
                'assignedto': {
                    'email': finalApprovers?.find((obj) => obj.type === 'Final Approval')?.email,
                    'givenname': finalApprovers?.find((obj) => obj.type === 'Final Approval')?.givenname,
                    'surname': finalApprovers?.find((obj) => obj.type === 'Final Approval')?.surname,
                },
                'assigneddate': new Date(),
                'createddate': new Date(),
                'createdby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'requestowner': task?.createdby,
                'activityfeed': [
                    {
                        'activitydate': new Date(),
                        'actionedby': {
                            'email': finalApprovers?.find((obj) => obj.type === 'Final Approval')?.email,
                            'givenname': finalApprovers?.find((obj) => obj.type === 'Final Approval')?.givenname,
                            'surname': finalApprovers?.find((obj) => obj.type === 'Final Approval')?.surname,
                        },
                        'action': 'assigned',
                        'comments': ''
                    }
                ]
            }

            docPromises.push(
                WriteDocument('requests', finalApprovalTask?.requestid, finalApprovalTask, true)
            );

            // LoA Approval Task
            const loaApprovalTask = {
                'requestid': requestIds[2],
                'parentrequestid': task?.parentrequestid,
                'tasktype': 'LoA Approval',
                'taskname': task?.requesttype,
                'taskdescription': `LoA Approval for ${task?.applicationname === '' || task?.applicationname === undefined ? task?.initiativename : task?.applicationname}`,
                'approverrole': finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.businessunit,
                'applicationname': task?.applicationname,
                'initiativename': task?.initiativename,
                'stage': '',
                'status': 'Initialised',
                'assignmentgroup': 'Demand Management',
                'requesttype': task?.requesttype,
                'activityfeedvisible': true,
                'approvalhubvisible': true,
                'region': '',
                'finalapprovalcomment': data?.comment,
                'assignedto': {
                    'email': finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.email,
                    'givenname': finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.givenname,
                    'surname': finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.surname
                },
                'assigneddate': new Date(),
                'createddate': new Date(),
                'createdby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'requestowner': task?.createdby,
                'activityfeed': [
                    {
                        'activitydate': new Date(),
                        'actionedby': {
                            'email': finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.email,
                            'givenname': finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.givenname,
                            'surname': finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.surname
                        },
                        'action': 'assigned',
                        'comments': ''
                    }
                ]
            }

            docPromises.push(
                WriteDocument('requests', loaApprovalTask?.requestid, loaApprovalTask, true)
            );

            // COO Approval Task
            const cooApprovalTask = {
                'requestid': requestIds[3],
                'parentrequestid': task?.parentrequestid,
                'tasktype': 'COO Approval',
                'taskname': task?.requesttype,
                'taskdescription': `COO Approval for ${task?.applicationname === '' || task?.applicationname === undefined ? task?.initiativename : task?.applicationname}`,
                'approverrole': 'COO',
                'applicationname': task?.applicationname,
                'initiativename': task?.initiativename,
                'stage': '',
                'status': 'Initialised',
                'assignmentgroup': 'Demand Management',
                'requesttype': task?.requesttype,
                'activityfeedvisible': true,
                'approvalhubvisible': true,
                'region': '',
                'finalapprovalcomment': data?.comment,
                'assignedto': {
                    'email': finalApprovers?.find((obj) => obj.type === 'COO Approval')?.email,
                    'givenname': finalApprovers?.find((obj) => obj.type === 'COO Approval')?.givenname,
                    'surname': finalApprovers?.find((obj) => obj.type === 'COO Approval')?.surname
                },
                'assigneddate': new Date(),
                'createddate': new Date(),
                'createdby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                },
                'requestowner': task?.createdby,
                'activityfeed': [
                    {
                        'activitydate': new Date(),
                        'actionedby': {
                            'email': finalApprovers?.find((obj) => obj.type === 'COO Approval')?.email,
                            'givenname': finalApprovers?.find((obj) => obj.type === 'COO Approval')?.givenname,
                            'surname': finalApprovers?.find((obj) => obj.type === 'COO Approval')?.surname
                        },
                        'action': 'assigned',
                        'comments': '',
                    }
                ]
            }

            docPromises.push(
                WriteDocument('requests', cooApprovalTask?.requestid, cooApprovalTask, true)
            );

            return Promise.all(docPromises)
            .then(() => {

                
            // Change Final Approval status in original task
                return WriteDocument('requests', task?.requestid, { 'sentfinalapprovals': true }, true)
                .then(() => {
                    
                    // Close Pane and Navigate to Next Stage
                    setPaneStatus('onload');
                    setPaneOpen(false);

                })
        
            })

        })
        .catch((error) => {

            setPaneStatus('error');
            console.log('error', error);

        })

    }

    // --------------------------------------------------
    //  HTML
    // --------------------------------------------------

    // ==================================================
    //  Hide Side Pane
    // ==================================================

    if (!paneOpen) return null;

    // ==================================================
    //  Show Side Pane  
    // ==================================================

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    if (paneStatus === 'onload') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container'>

                    {/* Form */}
                    <form className='grid grid-rows-[80px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Submit for Final Approval </h4>
                        </div>

                        {/* ===================================== */}
                        {/*  Body                                 */}
                        {/* ===================================== */}

                        {/* Form */}
                        <div className='w-[50vw] h-full flex flex-col gap-3 overflow-y-auto p-4 border border-solid shadow-inner'>

                            {/* Final Approvers */}
                            <div className='Form-Row'>

                                <label className='font-medium'> Final Approvers </label>

                                {/* List of Final Approvers (Read Only) */}
                                <div className='flex flex-wrap p-2 gap-1 border-solid border-1 border-[#D8D8D8] rounded-[5px] overflow-x-hidden'>

                                    <p className='m-0 py-1 px-3 bg-[#DDF3F5] text-[#1C96A3] text-[15px] rounded-lg'>
                                        {`${finalApprovers?.find((obj) => obj.type === 'COO Approval')?.givenname} ${finalApprovers?.find((obj) => obj.type === 'COO Approval')?.surname}`}
                                    </p>

                                    <p className='m-0 py-1 px-3 bg-[#DDF3F5] text-[#1C96A3] text-[15px] rounded-lg'>
                                        {`${finalApprovers?.find((obj) => obj.type === 'Final Approval')?.givenname} ${finalApprovers?.find((obj) => obj.type === 'Final Approval')?.surname}`}
                                    </p>

                                    <p className='m-0 py-1 px-3 bg-[#DDF3F5] text-[#1C96A3] text-[15px] rounded-lg'>
                                        {`${task?.financemanager?.givenname} ${task?.financemanager?.surname}`}
                                    </p>

                                    <p className='m-0 py-1 px-3 bg-[#DDF3F5] text-[#1C96A3] text-[15px] rounded-lg'>
                                        {`${finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.givenname} ${finalApprovers?.find((obj) => obj.type === 'LoA Approval')?.surname}`}
                                    </p>
                                </div>

                            </div>

                            {/* Comment */}
                            <div className='Form-Row'>

                                <label className='font-medium' > Comment <span className='text-[#C4314B]'>*</span> </label>

                                <textarea
                                    className={errors.comment ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                                    {...register('comment', {
                                        required: 'This field is required'
                                    })}
                                    placeholder='Enter a comment'
                                    type='text'
                                ></textarea>

                            </div>

                        </div>


                        {/* ===================================== */}
                        {/*  Footer                               */}
                        {/* ===================================== */}

                        {/* Buttons */}
                        <div className='px-4 py-3'>
                            <div className='flex flex-row gap-3'>

                                {/* Submit */}
                                <button className='Primary-Button' type='submit' value='submit' disabled={!watch('comment')}>
                                    Submit
                                </button>

                                {/* Cancel */}
                                <button className='Secondary-Button' onClick={() => {

                                    // Reset Form and Close Side Pane
                                    setPaneOpen(false);
                                    reset();

                                }}>
                                    Cancel
                                </button>

                            </div>
                        </div>
                    </form>

                </dialog >
            </div >
        );
    }

    // ---------------------------------------------------
    //  Pending
    // ---------------------------------------------------

    else if (paneStatus === 'pending') {
    return (
        <div className='Pane-Background'>
        <dialog className='Pane-Container flex justify-center items-center text-center'>
            <img alt='loading-circle-icon' src={Loading}></img>
        </dialog>
        </div>
    )
    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else if (paneStatus === 'error') {
        return (
        <div className='Pane-Background'>

            {/* Pane Container */}
            <dialog className='Pane-Container'>
            <div className='flex flex-col gap-2 w-full justify-center items-center'>

                <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
                <h4 className='mb-0'>Oops! Something went wrong.</h4>
                <p className='text-center leading-[1.7]'>
                An error occurred while we processed your request.
                </p>
                <button
                className='Primary-Button'
                onClick={() => {
                    // Reset Form and Close Pane
                    reset();
                    setPaneStatus('onload');
                    setPaneOpen(false);
                }}
                >
                    Close
                </button>

            </div>
            </dialog>
        </div>

        )
    }

}
